import type { Invoice, OwnedVehicle, OwnedVehicleDetail, VehicleStatus } from '@/api';
import { formatShortDate } from '@/utils/format';
import { platformLabel, portLabel, yardLabel } from '@/utils/vehicle-labels';

/*
  Що бачить дилер про куплене авто: етап для вкладок, наступний крок,
  маршрут на «сцені» картки (макети Paper «B · Мої авто — список» і «D ·
  Картка авто — сцена маршруту», погоджено 2026-11-04). Без розмітки — лише
  правила, щоб список і картка не розійшлися.
*/

/**
 * Етапи — вкладки списку. Клієнт просив «в дорозі, на складі, прибуло»:
 * «In the US» — від оплати до завантаження в контейнер (забір, склад, порт),
 * «At sea» — судно вийшло, «Arrived» — авто в порту призначення.
 */
export const VEHICLE_STAGES = [
	{ value: 'payment', label: 'Awaiting payment', statuses: ['AWAITING_PAYMENT'] },
	{ value: 'us', label: 'In the US', statuses: ['PAID', 'PICKUP_ORDERED', 'PICKED_UP', 'AT_WAREHOUSE', 'READY_FOR_LOADING', 'LOADED'] },
	{ value: 'sea', label: 'At sea', statuses: ['VESSEL_DEPARTED'] },
	{ value: 'arrived', label: 'Arrived', statuses: ['ARRIVED_AT_PORT', 'READY_FOR_PICKUP'] },
	{ value: 'done', label: 'Completed', statuses: ['DELIVERED'] },
] as const satisfies readonly { value: string; label: string; statuses: readonly VehicleStatus[] }[];

export type VehicleStage = (typeof VEHICLE_STAGES)[number]['value'];

export const VEHICLE_STAGE_VALUES: readonly string[] = VEHICLE_STAGES.map((s) => s.value);

export function stageOf(status: VehicleStatus): VehicleStage {
	const stage = VEHICLE_STAGES.find((s) => (s.statuses as readonly VehicleStatus[]).includes(status));
	return stage?.value ?? 'us';
}

/** Інвойс ще чекає грошей від дилера (поданий переказ — уже не його хід). */
export function isPayable(invoice: Invoice | null): boolean {
	return invoice !== null && (invoice.status === 'UNPAID' || invoice.status === 'OVERDUE');
}

/** Порт США маршруту: з інвойсу, інакше з копії лота; null — невідомий. */
function departureName(vehicle: OwnedVehicle | OwnedVehicleDetail): string | null {
	const port = vehicle.invoice?.routePort ?? ('lot' in vehicle ? (vehicle.lot?.departurePort ?? null) : null);
	return port ? portLabel(port) : null;
}

/** «New York → Jebel Ali»; без відомого порту США — лише куди. */
export function routeLine(vehicle: OwnedVehicle | OwnedVehicleDetail): { from: string; to: string } {
	return { from: departureName(vehicle) ?? 'US port', to: vehicle.destinationName };
}

/**
 * Наступний крок під статусом — те, чого сам статус не каже. `strong` —
 * рядок, який дилер шукає очима: дата прибуття або де забрати.
 */
export function nextStep(vehicle: OwnedVehicle): { text: string; strong: boolean } {
	const to = vehicle.destinationName;
	switch (vehicle.status) {
		case 'AWAITING_PAYMENT':
			return { text: 'Pickup starts after payment', strong: false };
		case 'PAID':
			return { text: 'US pickup is next', strong: false };
		case 'PICKUP_ORDERED':
			return { text: 'Truck is on the way to the yard', strong: false };
		case 'PICKED_UP':
			return { text: 'On the way to the warehouse', strong: false };
		case 'AT_WAREHOUSE':
			return { text: 'Waiting for a container', strong: false };
		case 'READY_FOR_LOADING':
			return { text: vehicle.containerNumber ? `Container ${vehicle.containerNumber} assigned` : 'Loading is next', strong: false };
		case 'LOADED':
			return { text: 'Departure is next', strong: false };
		case 'VESSEL_DEPARTED': {
			const eta = formatShortDate(vehicle.eta);
			return eta ? { text: `ETA ${to} · ${eta}`, strong: true } : { text: `On the way to ${to}`, strong: false };
		}
		case 'ARRIVED_AT_PORT':
			return { text: 'Pickup is being prepared', strong: false };
		case 'READY_FOR_PICKUP':
			return { text: `Collect at ${to}`, strong: true };
		case 'DELIVERED':
			return { text: 'Delivery complete', strong: false };
	}
}

const DAY = 86_400_000;
/** Дубай — UTC+4 без переходу на літній час: календарний день там зсунутий на 4 години. */
const DUBAI_OFFSET = 4 * 3_600_000;

/** Скільки календарних днів за Дубаєм між зараз і датою: «сьогодні о 18:00» — це 0, а не 1. */
function dubaiDaysUntil(iso: string, now: number): number {
	const day = (ms: number): number => Math.floor((ms + DUBAI_OFFSET) / DAY);
	return day(new Date(iso).getTime()) - day(now);
}

/** Підпис під сумою інвойсу. `tone` — колір рядка. */
export function invoiceNote(invoice: Invoice | null, now = Date.now()): { text: string; tone: 'muted' | 'success' | 'warning' | 'danger' } {
	if (!invoice) return { text: 'Invoice on the way', tone: 'muted' };
	if (invoice.status === 'PAID') return { text: invoice.paidAt ? `Paid ${formatShortDate(invoice.paidAt)}` : 'Paid', tone: 'success' };
	if (invoice.status === 'PAYMENT_SUBMITTED') return { text: 'Payment submitted', tone: 'muted' };
	const days = dubaiDaysUntil(invoice.dueAt, now);
	if (invoice.status === 'OVERDUE' || days < 0) {
		const late = Math.max(1, -days);
		return { text: `Overdue by ${late} day${late === 1 ? '' : 's'}`, tone: 'danger' };
	}
	if (days === 0) return { text: 'Due today', tone: 'warning' };
	return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, tone: 'warning' };
}

/** Подія таймлайну, коли оператор не лишив примітки: щоб рядок не стояв голим. */
export function eventSentence(status: VehicleStatus, destinationName: string): string {
	const map: Record<VehicleStatus, string> = {
		AWAITING_PAYMENT: 'Invoice issued.',
		PAID: 'Payment confirmed.',
		PICKUP_ORDERED: 'Pickup ordered from the auction yard.',
		PICKED_UP: 'Collected from the auction yard.',
		AT_WAREHOUSE: 'Received at the Sirius warehouse.',
		READY_FOR_LOADING: 'Ready to load into a container.',
		LOADED: 'Loaded into the container.',
		VESSEL_DEPARTED: 'The vessel left the US port.',
		ARRIVED_AT_PORT: `Arrived at ${destinationName}.`,
		READY_FOR_PICKUP: `Ready for pickup in ${destinationName}.`,
		DELIVERED: 'Collected. Delivery complete.',
	};
	return map[status];
}

export interface RouteStop {
	name: string;
	detail: string;
}

/** Де авто на маршруті: на зупинці або в дорозі між зупинкою `index` і наступною. */
export type RoutePosition = { at: 'stop'; index: number } | { at: 'leg'; index: number; label: string };

const POSITION: Record<VehicleStatus, RoutePosition> = {
	AWAITING_PAYMENT: { at: 'stop', index: 0 },
	PAID: { at: 'stop', index: 0 },
	PICKUP_ORDERED: { at: 'stop', index: 0 },
	PICKED_UP: { at: 'leg', index: 0, label: 'On the truck' },
	AT_WAREHOUSE: { at: 'stop', index: 1 },
	READY_FOR_LOADING: { at: 'stop', index: 1 },
	LOADED: { at: 'stop', index: 2 },
	VESSEL_DEPARTED: { at: 'leg', index: 2, label: 'At sea' },
	ARRIVED_AT_PORT: { at: 'stop', index: 3 },
	READY_FOR_PICKUP: { at: 'stop', index: 3 },
	DELIVERED: { at: 'stop', index: 3 },
};

/**
 * Маршрут сцени: майданчик → склад Sirius → порт США → порт призначення.
 * Дата стоїть лише біля того, що справді сталося (є подія в таймлайні) —
 * той самий інваріант, що й у стовпчику кроків адмінки.
 */
export function routePlan(vehicle: OwnedVehicleDetail): { stops: RouteStop[]; position: RoutePosition } {
	const at = (status: VehicleStatus): string | null => {
		const event = vehicle.timeline.find((e) => e.status === status);
		return event ? formatShortDate(event.createdAt) : null;
	};
	const yard = vehicle.lot?.locationDisplay ? yardLabel(vehicle.lot.locationDisplay) : null;
	const port = departureName(vehicle);
	const eta = formatShortDate(vehicle.eta);

	const picked = at('PICKED_UP');
	const ordered = at('PICKUP_ORDERED');
	const warehouse = at('AT_WAREHOUSE');
	const loaded = at('LOADED');
	const arrived = at('ARRIVED_AT_PORT');

	return {
		stops: [
			{
				name: yard ?? `${vehicle.platform ? platformLabel(vehicle.platform) : 'Auction'} yard`,
				detail: picked
					? `Picked up ${picked}`
					: ordered
						? `Pickup ordered ${ordered}`
						: vehicle.status === 'AWAITING_PAYMENT'
							? 'Pickup after payment'
							: 'Pickup next',
			},
			{ name: 'Sirius warehouse', detail: warehouse ? `Received ${warehouse}` : 'Upcoming' },
			{ name: port ? `Port of ${port}` : 'US port', detail: loaded ? `Loaded ${loaded}` : 'Upcoming' },
			{ name: vehicle.destinationName, detail: arrived ? `Arrived ${arrived}` : eta ? `ETA ${eta}` : 'ETA after loading' },
		],
		position: POSITION[vehicle.status],
	};
}

/**
 * Головне число сцени праворуч. Є ETA і авто ще в дорозі — дата прибуття;
 * прибуло — коли; інакше — сам етап і наступний крок.
 */
export function stageHeadline(vehicle: OwnedVehicleDetail, statusLabel: string, now = Date.now()): { label: string; value: string; caption: string; isDate: boolean } {
	const to = vehicle.destinationName;
	const changed = formatShortDate(vehicle.statusChangedAt);

	if (vehicle.status === 'DELIVERED') return { label: 'Completed', value: changed ?? '—', caption: `Collected in ${to}`, isDate: true };
	if (vehicle.status === 'ARRIVED_AT_PORT' || vehicle.status === 'READY_FOR_PICKUP') {
		const arrived = vehicle.timeline.find((e) => e.status === 'ARRIVED_AT_PORT');
		return { label: `Arrived in ${to}`, value: formatShortDate(arrived?.createdAt ?? vehicle.statusChangedAt) ?? '—', caption: statusLabel, isDate: true };
	}
	if (vehicle.eta) {
		const days = Math.max(0, dubaiDaysUntil(vehicle.eta, now));
		const when = days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`;
		return { label: `Arrives in ${to}`, value: formatShortDate(vehicle.eta) ?? '—', caption: `${when} · ${statusLabel.toLowerCase()} ${changed ?? ''}`.trim(), isDate: true };
	}
	return { label: 'Now', value: statusLabel, caption: nextStep(vehicle).text, isDate: false };
}
